/**
 * =============================================================================
 * ROASTBREW - VIDEO VIEW ROUTER
 * =============================================================================
 *
 * Picks a video page from TWO independent inputs:
 *
 *   1. ENGINE — the capability verdict's `player`. Which DRM engine can drive
 *      playback here. Never a layout decision.
 *   2. FORM FACTOR — phone / tablet / desktop. Which layout and control scheme.
 *      Never an engine decision.
 *
 * Six destinations, one per (engine x form factor):
 *
 *                | phone                        | TABLET                        | desktop
 *   -------------+------------------------------+-------------------------------+----------------------
 *   videojs      | VideoViewPageFairPlayMobile  | VideoViewPageFairPlayTablet   | VideoViewPageFairPlay
 *   (FairPlay)   | iOS Safari / Chrome / FF     | iPad, any browser             | macOS Safari
 *   -------------+------------------------------+-------------------------------+----------------------
 *   shaka        | VideoViewPageMobile          | VideoViewPageTablet           | VideoViewPage
 *   (Widevine)   | Android phones               | Android tablets               | desktop Chrome/FF/Edge
 *
 * ── WHY TABLET USES `device`, NOT WIDTH ──────────────────────────────────────
 * AppLayout's isTablet is `width >= 768 && width < 1024`. An iPad in LANDSCAPE
 * is 1024px, so a width-based test would flip it to the desktop page on rotation
 * — tearing down and remounting the player mid-lesson, mid-stream.
 *
 * `device` from the capability verdict is HARDWARE class, computed once per
 * session from UA plus touch points (it also unmasks the iPad that reports
 * itself as a Mac). It cannot change on rotation, so the page cannot swap under
 * a playing video.
 *
 * This is the one place `device` is allowed to affect routing, and it selects a
 * LAYOUT only — the engine still comes from `player`. The header of
 * utils/playbackCapabilityDetection.js says `device` is copy-only; that was
 * written before tablet pages existed and this is the documented exception.
 *
 * ── WHAT THE TABLET PAGES ARE ────────────────────────────────────────────────
 * Desktop layout, mobile player. A tablet has a desktop-sized screen and a
 * phone's input, so it needs the two-column grid AND 48px touch targets. Before
 * they existed this router only tested isMobile, so every iPad got the desktop
 * page: hover-dependent controls, ~27px targets, and a tab strip needing ~107px
 * per tab in an 80px slot.
 *
 * ⚠️ EACH ENGINE NOW EXISTS IN THREE FILES. A DRM change must be applied to all
 * three in its row. This is scaffolding; the end state is one page per engine
 * with a swappable control layer.
 *
 * RELEASE C: the form factor comes from `device` alone (videoViewRouting.js). The old
 * `isMobile ||` width override is gone: narrow windows keep their own page (Release B2 layouts).
 *
 * PLACEMENT: the /video/:id route inside <RequireAuth><AuthenticatedLayout>.
 * =============================================================================
 */

import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePlaybackCapability } from './PlaybackCapabilityContext';
import { pickVideoPage } from './videoViewRouting';
import VideoViewPage from './VideoViewPage';
import VideoViewPageTablet from './VideoViewPageTablet';
import VideoViewPageMobile from './VideoViewPageMobile';
import VideoViewPageFairPlay from './VideoViewPageFairPlay';
import VideoViewPageFairPlayTablet from './VideoViewPageFairPlayTablet';
import VideoViewPageFairPlayMobile from './VideoViewPageFairPlayMobile';

const PAGES = { VideoViewPage, VideoViewPageTablet, VideoViewPageMobile, VideoViewPageFairPlay, VideoViewPageFairPlayTablet, VideoViewPageFairPlayMobile };

export default function VideoViewRouter(props) {
  const { player, device } = usePlaybackCapability();
  // Release C (3.2): the page follows the device only (see videoViewRouting.js). Window width no longer forces the
  // phone page: the desktop page has its very small layout and the tablet page its narrow layout (Release B2).
  const pageName = pickVideoPage(player, device);
  const Page = PAGES[pageName];

  // Release C (3.4): one routing log per lesson, beside PlaybackCapabilityContext's '[playback-capability]' log.
  const { id } = useParams();
  useEffect(() => {
    console.info('[video-routing]', { lesson: id, page: pageName, player, device, window: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '' });
  }, [id, pageName, player, device]);

  return <Page {...props} />;
}
